import Cookies from 'js-cookie';

import { HTTP_STATUS } from '@/common/http-status';
import { browserAuthPath, sessionCookieName } from '@/lib/guest-auth/constants';
import type { GatedCall } from '@/lib/guest-auth/types';
import { noteSession, type SessionNote } from '@/lib/history-key/session';
import {
  readJsonFromLocalStorage,
  safeLocalStorage,
  writeJsonToLocalStorage,
} from '@/lib/storage/safe-storage';
import { fetchWithTimeout } from '@/lib/utils/api-client';
import { safeRandomUUID } from '@/lib/utils/safe-uuid';
import { isWebsiteBrowser } from '@/lib/website-auth/browser';
import { WEBSITE_SESSION_TIMEOUT_MS } from '@/lib/website-auth/constants';

// Set before the sign-out request goes out, and removed once the server confirms it: a sign-out
// the server never heard (offline, closed tab) is retried on the next lookup, and no tab shows the
// guest signed in meanwhile. Each sign-out writes its own token, so a request answering late can't
// clear the flag of a newer sign-out. It also names the session it ends, and the server ends no
// other: every sign-in mints a new binding and ends the old session, so a retry after one must not
// end the session it made.
function signOutPendingKey(tenant: string): string {
  return `bazak-guest-auth-signing-out:${tenant}`;
}

interface PendingSignOut {
  token: string;
  session?: string;
}

// This tab's sign-out when storage refused its flag, so it is still retried and a late answer is
// still told apart from a newer one. With storage blocked no other tab can take it over.
const unpersistedSignOuts = new Map<string, PendingSignOut>();

function storedSignOut(tenant: string): PendingSignOut | null {
  const stored = readJsonFromLocalStorage<Partial<PendingSignOut> | null>({
    key: signOutPendingKey(tenant),
    fallback: null,
  });
  if (typeof stored?.token !== 'string') return null;
  const { token, session } = stored;
  return { token, ...(typeof session === 'string' && { session }) };
}

function pendingSignOut(tenant: string): PendingSignOut | null {
  return storedSignOut(tenant) ?? unpersistedSignOuts.get(tenant) ?? null;
}

function callAuthRoute({
  action,
  options,
  timeout,
  search = '',
}: {
  action: Parameters<typeof browserAuthPath>[0];
  options: RequestInit;
  timeout: number;
  search?: string;
}): Promise<Response> {
  return fetchWithTimeout({
    url: `${browserAuthPath(action)}${search}`,
    options,
    timeout,
    retries: 0,
  });
}

export interface SessionLookup {
  signedIn?: boolean;
  /** Opaque name of the signed-in session: a pending sign-out naming another was overtaken by a
   *  sign-in. */
  session?: string;
  /** Website auth only: the session is an in-chat sign-in standing in for the website login. */
  inChat?: boolean;
  /** `user-<hash>`: who the browser's account conversations belong to. */
  scope?: string;
  /** Base64url AES key for that owner's conversations; held in memory only. */
  historyKey?: string;
  /** A newer lookup started before this one answered; the answer describes the past. */
  superseded?: boolean;
  /** Website auth only: why the browser is signed out (a `WEBSITE_ERROR` code). */
  reason?: string;
}

let latestLookup = 0;
let inflight:
  | { lookup: number; tenant: string; introspectNow: boolean; promise: Promise<SessionLookup> }
  | undefined;

/** Reads the session. Concurrent callers share one request, so only a sign-out meanwhile can
 *  make an answer stale, and only the most recently started lookup gets to change the held key.
 *  A `fresh` lookup never joins one in flight: that request may predate the change it asks about. */
export function lookupSession({
  tenant,
  fresh = false,
  introspectNow = false,
}: {
  tenant: string;
  fresh?: boolean;
  /** The server asks the authorization server now rather than answer from its last check. */
  introspectNow?: boolean;
}): Promise<SessionLookup> {
  // It joins only a lookup that also asks now: an ordinary one in flight may answer from the
  // server's remembered check.
  if (
    !fresh &&
    inflight?.lookup === latestLookup &&
    inflight.tenant === tenant &&
    (!introspectNow || inflight.introspectNow)
  )
    return inflight.promise;
  const lookup = ++latestLookup;
  const promise = readSession({ tenant, lookup, introspectNow }).finally(function forgetInflight() {
    if (inflight?.lookup === lookup) inflight = undefined;
  });
  inflight = { lookup, tenant, introspectNow, promise };
  return promise;
}

async function readSession({
  tenant,
  lookup,
  introspectNow,
}: {
  tenant: string;
  lookup: number;
  introspectNow: boolean;
}) {
  function superseded() {
    return lookup !== latestLookup;
  }
  function note(body: SessionNote) {
    return superseded() ? undefined : noteSession(body);
  }
  function answer(session: SessionLookup): SessionLookup {
    return superseded() ? { ...session, superseded: true } : session;
  }
  const website = isWebsiteBrowser(tenant);
  const pendingWhenAsked = pendingSignOut(tenant);
  // Locked before asking, so a tab whose check fails still shows the sign-out. A website login is
  // the host's own, and only the answer says whether an in-chat one stands in for it.
  if (!website && pendingWhenAsked) await note(null);
  const response = await callAuthRoute({
    action: 'token',
    options: { cache: 'no-store' },
    timeout: WEBSITE_SESSION_TIMEOUT_MS,
    ...(introspectNow && { search: '?introspect' }),
  });
  if (response.status === HTTP_STATUS.UNAUTHORIZED && website) {
    await note(null);
    return answer({ signedIn: false });
  }
  if (!response.ok) throw new Error(`Session lookup failed with ${response.status}`);
  const session: SessionLookup = await response.json();
  // A website login is the host's own and never ours to sign out of.
  if (session.signedIn && (!website || session.inChat)) {
    const pending = pendingSignOut(tenant);
    // An answer for another session proves a sign-in replaced the pending sign-out's session only
    // when that sign-out was already pending as this lookup asked, and the answer isn't stale: a
    // sign-out made since may be for the very session a late answer is behind on.
    if (
      pending &&
      !superseded() &&
      pending.token === pendingWhenAsked?.token &&
      endedBySignIn({ pending, session })
    ) {
      clearSignOut(tenant);
    } else if (pending) {
      void finishSignOut({ tenant, pending }).catch(function retriedOnNextLookup() {});
      await note(null);
      return answer({ signedIn: false });
    }
  }
  try {
    await note(session);
  } catch (error) {
    if (!session.signedIn || website) throw error;
    // Chat goes on signed in while history stays locked; website sign-in retries.
    console.warn('History key unavailable; continuing with the authenticated session');
  }
  return answer(session);
}

// The flag names another session than the one answering: a sign-in since replaced it, and `/signin`
// ended the session the sign-out meant.
function endedBySignIn({ pending, session }: { pending: PendingSignOut; session: SessionLookup }) {
  return Boolean(pending.session && session.session && pending.session !== session.session);
}

function forgetSession(): Promise<void> {
  latestLookup++;
  return noteSession(null);
}

/** A refused request means the session is gone; a lookup already in flight answers for the past. */
export function noteUnauthorized(): void {
  void forgetSession();
}

// Other tabs hear about an in-chat sign-in or sign-out through the `storage` event, which never
// fires in the tab that wrote; every tab shares the one session, so each rechecks at once.
export function sessionChangeKey(tenant: string): string {
  return `bazak-session-changed:${tenant}`;
}

function announceSessionChange(tenant: string): void {
  safeLocalStorage.setItem(sessionChangeKey(tenant), String(Date.now()));
}

const signingOut = new Map<string, { token: string; request: Promise<void> }>();

function clearSignOut(tenant: string): void {
  safeLocalStorage.removeItem(signOutPendingKey(tenant));
  unpersistedSignOuts.delete(tenant);
}

async function sendSignOut({
  tenant,
  pending: { token, session },
}: {
  tenant: string;
  pending: PendingSignOut;
}): Promise<void> {
  const response = await callAuthRoute({
    action: 'signout',
    options: {
      method: 'POST',
      ...(session && {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session }),
      }),
    },
    timeout: WEBSITE_SESSION_TIMEOUT_MS,
  });
  // A 404 means guest sign-in is off for this tenant: no session is left to end, and throwing
  // would hold every later chat turn behind a sign-out that can never land.
  if (!response.ok && response.status !== HTTP_STATUS.NOT_FOUND) {
    throw new Error(`Sign-out failed with ${response.status}`);
  }
  // Not ours any more: a newer sign-out still needs its own request, or a lookup found a sign-in
  // had replaced this session; that sign-in's lookups are current, so they stand.
  if (pendingSignOut(tenant)?.token !== token) return;
  clearSignOut(tenant);
  // Lookups sent before the server ended the session would otherwise answer signed in.
  latestLookup++;
}

/** One sign-out request per pending sign-out, tenant and tab at a time. The sign-out comes from
 *  the caller when it has one: with storage blocked the flag never lands, and the request must
 *  still go. */
function finishSignOut({
  tenant,
  pending = pendingSignOut(tenant),
}: {
  tenant: string;
  pending?: PendingSignOut | null;
}): Promise<void> {
  if (pending === null) return Promise.resolve();
  const { token } = pending;
  const inFlight = signingOut.get(tenant);
  if (inFlight?.token === token) return inFlight.request;
  const request = sendSignOut({ tenant, pending }).finally(function forgetRequest() {
    if (signingOut.get(tenant)?.request === request) signingOut.delete(tenant);
  });
  signingOut.set(tenant, { token, request });
  return request;
}

/** Signed out here at once, in every tab: the pending flag goes up and the other tabs hear of it
 *  before the server call, which is retried until it lands. A refused request still throws so the
 *  caller can say so. */
export async function signOut(tenant: string): Promise<void> {
  const session = browserSessionHandle(tenant);
  const pending: PendingSignOut = { token: safeRandomUUID(), ...(session && { session }) };
  writeJsonToLocalStorage({ key: signOutPendingKey(tenant), value: pending });
  if (storedSignOut(tenant)?.token === pending.token) unpersistedSignOuts.delete(tenant);
  else unpersistedSignOuts.set(tenant, pending);
  announceSessionChange(tenant);
  const signedOut = finishSignOut({ tenant, pending });
  // Awaited below; this only keeps a quick refusal from reading as unhandled meanwhile.
  signedOut.catch(function awaitedBelow() {});
  await forgetSession();
  await signedOut;
  // A lookup this tab had in flight can land after a sibling's request cleared the flag, and note
  // the session that just ended.
  await checkSession(tenant);
  announceSessionChange(tenant);
}

// The session this browser's binding names now, which may be newer than this tab's last lookup
// shows. Missing where script can't read cookies (a sandboxed frame throws): the sign-out then
// ends whatever session the binding names.
function browserSessionHandle(tenant: string): string | undefined {
  try {
    return Cookies.get(sessionCookieName(tenant)) || undefined;
  } catch {
    return undefined;
  }
}

/** Sends a pending sign-out before anything else goes out under the session: until the server
 *  ends it, the cookie still names the guest who signed out. Throws if the server refuses. */
export function settleSignOut(tenant: string): Promise<void> {
  return finishSignOut({ tenant });
}

/** One fresh look at the session, keeping what we hold when the endpoint cannot answer. */
export async function checkSession(tenant: string): Promise<void> {
  try {
    await lookupSession({ tenant, fresh: true });
  } catch {
    // Unreachable or failing endpoint: neither a sign-in nor a sign-out.
  }
}

export async function isSignedIn({
  tenant,
  introspectNow = false,
}: {
  tenant: string;
  introspectNow?: boolean;
}): Promise<boolean> {
  try {
    return Boolean((await lookupSession({ tenant, introspectNow })).signedIn);
  } catch {
    return false;
  }
}

// Only the verification nonce and lifetime reach the form; state and PKCE stay server-side.
// `cookiesBlocked`: the browser dropped the binding cookie, so no sign-in here could ever finish.
export type BrowserSignInAttempt =
  | {
      verificationId: string;
      expiresInSeconds: number;
      authorizeUrl?: never;
      cookiesBlocked?: never;
    }
  | {
      authorizeUrl: string;
      verificationId?: never;
      expiresInSeconds?: never;
      cookiesBlocked?: never;
    }
  | {
      cookiesBlocked: true;
      verificationId?: never;
      authorizeUrl?: never;
      expiresInSeconds?: never;
    };

// Sized for the server's slowest normal path: discovery, the authorize or callback hop and the code
// exchange run back to back, each under AUTH_SERVER_TIMEOUT_SECONDS or AUTH_SERVER_HOP_TIMEOUT_MS,
// plus its Redis calls. Nothing caps their total, so a sign-in can still finish after this gives
// up; the session check that follows a failed completion picks it up.
const SIGN_IN_TIMEOUT_MS = 60_000;
const COOKIE_CHECK_TIMEOUT_MS = 15_000;

async function cookieArrived(): Promise<boolean> {
  const response = await callAuthRoute({
    action: 'cookie-check',
    options: { cache: 'no-store' },
    timeout: COOKIE_CHECK_TIMEOUT_MS,
  });
  if (!response.ok) throw new Error(`Cookie check failed with ${response.status}`);
  return Boolean(((await response.json()) as { present?: boolean }).present);
}

export async function startSignIn({
  gate,
  threadId,
  distinctId,
}: {
  gate?: GatedCall;
  threadId?: string;
  distinctId?: string;
} = {}): Promise<BrowserSignInAttempt | undefined> {
  const response = await callAuthRoute({
    action: 'signin',
    options: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gate, threadId, distinctId }),
    },
    timeout: SIGN_IN_TIMEOUT_MS,
  });
  if (!response.ok) return undefined;
  const { verificationId, expiresInSeconds, authorizeUrl } = (await response.json()) as {
    verificationId?: string;
    expiresInSeconds?: number;
    authorizeUrl?: string;
  };
  if (!(await cookieArrived())) return { cookiesBlocked: true };
  if (authorizeUrl && URL.canParse(authorizeUrl)) return { authorizeUrl };
  // Without a lifetime, the form could keep accepting codes for an expired attempt.
  if (!verificationId || !Number.isFinite(expiresInSeconds)) return undefined;
  return { verificationId, expiresInSeconds: expiresInSeconds as number };
}

// The server reconstructs the callback; the browser supplies no destination.
export async function completeSignIn({
  tenant,
  verificationId,
  completion,
}: {
  tenant: string;
  verificationId: string;
  completion?: string;
}): Promise<boolean> {
  const response = await callAuthRoute({
    action: 'complete',
    options: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ verificationId, ...(completion && { completion }) }),
    },
    timeout: SIGN_IN_TIMEOUT_MS,
  });
  if (response.ok) announceSessionChange(tenant);
  return response.ok;
}
